import { logError, logWarning } from '@databases/logs';
import { z } from 'zod';

import { invokeObjectResolver } from '@canvas/atlassian-service/datasource';

export const PROVIDER_KEY = 'jira-object-provider';

export const SmartLinkActionType = {
  GetStatusTransitionsAction: 'GetStatusTransitionsAction',
  StatusUpdateAction: 'StatusUpdateAction',
} as const;

export const TransitionItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  appearance: z.string().optional(),
});

export type TransitionItem = z.infer<typeof TransitionItemSchema>;

type GetStatusTransitionsResponse = {
  transitions?: Array<{
    id: string;
    name: string;
    appearance?: string;
  }>;
};

const InvokeRequestSchema = z.object({
  action: z.object({
    actionType: z.string(),
    resourceIdentifiers: z.record(z.any()),
    payload: z.optional(z.any()),
  }),
  providerKey: z.string(),
});

export async function getStatusTransitions(params: {
  issueKey: string;
  hostname?: string;
  cloudId?: string;
}): Promise<TransitionItem[]> {
  try {
    const request = InvokeRequestSchema.parse({
      action: {
        actionType: SmartLinkActionType.GetStatusTransitionsAction,
        resourceIdentifiers: {
          issueKey: params.issueKey,
          hostname: params.hostname,
          cloudId: params.cloudId,
        },
      },
      providerKey: PROVIDER_KEY,
    });

    const response = await invokeObjectResolver<GetStatusTransitionsResponse>({
      providerKey: request.providerKey,
      action: request.action,
    });

    const items: TransitionItem[] = (response?.transitions ?? []).map((t) => ({
      id: t.id,
      name: t.name,
      appearance: t.appearance,
    }));

    if (!items.length) {
      logWarning(new Error('jira-status/transitions:empty'));
    }

    return items.sort(
      (a: TransitionItem, b: TransitionItem) =>
        (a.appearance || '').localeCompare(b.appearance || '') ||
        a.name.localeCompare(b.name)
    );
  } catch {
    logError('jira-status/transitions:error', new Error('transitions failed'));
    return [];
  }
}

export async function updateIssueStatus(params: {
  issueKey: string;
  hostname?: string;
  cloudId?: string;
  newStatusId: string;
}): Promise<void> {
  try {
    const request = InvokeRequestSchema.parse({
      action: {
        actionType: SmartLinkActionType.StatusUpdateAction,
        resourceIdentifiers: {
          issueKey: params.issueKey,
          hostname: params.hostname,
          cloudId: params.cloudId,
        },
        payload: { newStatusId: params.newStatusId },
      },
      providerKey: PROVIDER_KEY,
    });

    await invokeObjectResolver<unknown>({
      providerKey: request.providerKey,
      action: request.action,
    });
  } catch {
    logError('jira-status/invoke:error', new Error('invoke failed'));
    throw new Error('invoke failed');
  }
}
